import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Airplane, BuildingApartment, MapTrifold, Boat, CurrencyDollar, FloppyDisk,
  Share, Plus, Trash, Eye, CircleNotch, ArrowLeft, ClipboardText, Check, Info,
  Car, ShieldCheck, CalendarBlank, Storefront, ArrowSquareOut, EyeSlash,
  ArrowUp, ArrowDown, MagnifyingGlass, Copy, Lock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { DayByDayTab } from "@/components/DayByDayTab";
import { FlightLookupDialog } from "@/components/FlightLookupDialog";
import { useAuth } from "@/contexts/AuthContext";

const CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "PHP", "JPY", "SGD",
  "AED", "MXN", "THB", "HKD", "CNY", "KRW", "CHF", "NZD",
];

function newId() { return Math.random().toString(36).slice(2, 10); }

function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-3">
        <Icon size={26} weight="duotone" className="text-[#D4AF37]" />
        <div>
          <h2 className="font-serif text-2xl text-white">{title}</h2>
          {subtitle && <p className="text-xs text-white/40 font-sans">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function Field({ label, children, className = "", hint = "" }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-white/50 mb-1.5 block font-sans">{label}</Label>
      {hint && <p className="text-[11px] text-white/40 font-sans mb-1.5 -mt-0.5 leading-snug">{hint}</p>}
      {children}
    </div>
  );
}

function ItemCard({ index, onRemove, children, testid }) {
  return (
    <div className="border border-[#D4AF37]/20 bg-black/30 rounded-lg p-5 mb-4" data-testid={testid}>
      <div className="flex justify-between items-center mb-4">
        <span className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-sans">#{index + 1}</span>
        <Button size="sm" variant="ghost" onClick={onRemove} className="text-white/50 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`remove-${testid}`}>
          <Trash size={15} weight="duotone" />
        </Button>
      </div>
      {children}
    </div>
  );
}

const inputCls = "bg-black/40 border-white/10 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 text-white font-sans";

export default function ItineraryBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [itin, setItin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [lookupIdx, setLookupIdx] = useState(null); // index of flight being looked up, or null

  // Read-only when an agent is viewing a template — only admins can edit a template directly.
  const isTemplate = !!itin?.is_template;
  const isReadOnly = isTemplate && user?.role !== "admin";

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const { data } = await api.post(`/itineraries/${id}/duplicate`);
      toast.success("Template duplicated — start customizing!");
      navigate(`/itineraries/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to duplicate");
    } finally {
      setDuplicating(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/itineraries/${id}`);
        setItin(data);
      } catch {
        toast.error("Itinerary not found");
        navigate("/dashboard");
      }
    })();
  }, [id, navigate]);

  if (!itin) {
    return (
      <AgentLayout>
        <div className="py-20 flex justify-center">
          <CircleNotch size={28} className="text-[#D4AF37] animate-spin" />
        </div>
      </AgentLayout>
    );
  }

  const update = (patch) => setItin((prev) => ({ ...prev, ...patch }));
  const updateList = (key, index, patch) => {
    const arr = [...(itin[key] || [])];
    arr[index] = { ...arr[index], ...patch };
    update({ [key]: arr });
  };
  const removeFromList = (key, index) => {
    const arr = [...(itin[key] || [])];
    arr.splice(index, 1);
    update({ [key]: arr });
  };
  const addTo = (key, item) => update({ [key]: [...(itin[key] || []), { id: newId(), ...item }] });

  const updatePricing = (patch) => update({ pricing: { ...itin.pricing, ...patch } });
  const updatePricingItem = (index, patch) => {
    const items = [...(itin.pricing.items || [])];
    items[index] = { ...items[index], ...patch };
    updatePricing({ items });
  };
  const removePricingItem = (index) => {
    const items = [...(itin.pricing.items || [])];
    items.splice(index, 1);
    updatePricing({ items });
  };
  const pricingTotal = (itin.pricing.items || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...itin, pricing: { ...itin.pricing, total: pricingTotal } };
      // Strip empty inclusion/exclusion lines on save (kept during editing so
      // pressing Enter mid-list doesn't collapse blank rows).
      payload.inclusions = (payload.inclusions || []).filter((s) => (s || "").trim());
      payload.exclusions = (payload.exclusions || []).filter((s) => (s || "").trim());
      // Coerce any string/blank numeric supplier_link fields to real numbers so
      // Pydantic doesn't reject weird inputs like "0138.37" or "" typed in
      // the internal commission field.
      payload.supplier_links = (payload.supplier_links || []).map((sl) => ({
        ...sl,
        expected_commission: Number.parseFloat(sl.expected_commission) || 0,
      }));
      delete payload.id; delete payload.agent_id; delete payload.share_token;
      delete payload.created_at; delete payload.updated_at;
      const { data } = await api.put(`/itineraries/${id}`, payload);
      setItin(data);
      toast.success("Saved");
    } catch (e) {
      // Surface the actual backend reason instead of a generic "Failed to save".
      // Pydantic 422 errors arrive as an array in `detail`; flatten them for the toast.
      const detail = e?.response?.data?.detail;
      let msg = "Failed to save";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        msg = detail
          .map((d) => `${(d.loc || []).join(".")}: ${d.msg}`)
          .join(" | ");
      } else if (e?.message) msg = e.message;
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = `${window.location.origin}/trip/${itin.share_token}`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <AgentLayout>
      {/* Top bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-white/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 -ml-3"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} weight="duotone" /> Back
          </Button>
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Editing</p>
            <h1 className="font-serif text-2xl text-white truncate max-w-md" data-testid="builder-title">
              {itin.title || "Untitled trip"}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a href={`/trip/${itin.share_token}`} target="_blank" rel="noreferrer">
            <Button variant="ghost" className="text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="preview-btn">
              <Eye size={18} weight="duotone" /> Preview
            </Button>
          </a>
          {!isReadOnly && (
            <Button
              onClick={copyShareLink}
              variant="outline"
              className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] bg-transparent"
              data-testid="copy-share-btn"
            >
              {copied ? <Check size={18} weight="bold" /> : <Share size={18} weight="duotone" />}
              <span className="hidden sm:inline">{copied ? "Copied" : "Share link"}</span>
            </Button>
          )}
          {isReadOnly ? (
            <Button
              onClick={handleDuplicate}
              disabled={duplicating}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-10 px-5"
              data-testid="duplicate-template-btn"
            >
              {duplicating ? <CircleNotch size={18} className="animate-spin" /> : <><Copy size={18} weight="duotone" /> Use this template</>}
            </Button>
          ) : (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-[0.25em] h-10 px-5"
              data-testid="save-btn"
            >
              {saving ? <CircleNotch size={18} className="animate-spin" /> : <><FloppyDisk size={18} weight="duotone" /> Save</>}
            </Button>
          )}
        </div>
      </div>

      {/* Read-only template banner */}
      {isReadOnly && (
        <div className="mb-6 border border-[#D4AF37]/30 bg-[#D4AF37]/5 rounded-lg px-5 py-4 flex items-start gap-3" data-testid="template-readonly-banner">
          <Lock size={18} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-white font-sans">
              <span className="font-semibold text-[#D4AF37]">Sample Template — read only.</span>{" "}
              Use this as a guide for how to structure a premium itinerary. Click{" "}
              <span className="text-[#D4AF37]">"Use this template"</span> to create your own editable copy.
            </p>
          </div>
          <Button
            onClick={handleDuplicate}
            disabled={duplicating}
            className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-[10px] uppercase tracking-widest h-9 px-3 shrink-0 hidden md:inline-flex"
          >
            <Copy size={12} weight="duotone" /> Use template
          </Button>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-ink-surface border border-white/10 p-1 h-auto flex flex-wrap gap-1 mb-8" data-testid="builder-tabs">          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Info size={14} weight="duotone" className="mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="days" data-testid="tab-days" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <CalendarBlank size={14} weight="duotone" className="mr-1.5" /> Day by Day
            {(itin.days?.length || 0) > 0 && <span className="ml-1.5 opacity-60">({itin.days.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="flights" data-testid="tab-flights" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Airplane size={14} weight="duotone" className="mr-1.5" /> Flights
          </TabsTrigger>
          <TabsTrigger value="hotels" data-testid="tab-hotels" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <BuildingApartment size={14} weight="duotone" className="mr-1.5" /> Hotels
          </TabsTrigger>
          <TabsTrigger value="tours" data-testid="tab-tours" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <MapTrifold size={14} weight="duotone" className="mr-1.5" /> Tours
          </TabsTrigger>
          <TabsTrigger value="cruise" data-testid="tab-cruise" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Boat size={14} weight="duotone" className="mr-1.5" /> Cruise
          </TabsTrigger>
          <TabsTrigger value="transportation" data-testid="tab-transportation" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Car size={14} weight="duotone" className="mr-1.5" /> Transport
          </TabsTrigger>
          <TabsTrigger value="protection" data-testid="tab-protection" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <ShieldCheck size={14} weight="duotone" className="mr-1.5" /> Protection
          </TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <CurrencyDollar size={14} weight="duotone" className="mr-1.5" /> Pricing
          </TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-white/60 px-4 py-2 text-xs uppercase tracking-widest font-sans">
            <Storefront size={14} weight="duotone" className="mr-1.5" /> Suppliers
          </TabsTrigger>
        </TabsList>

        {/* Read-only veil for templates: blocks all input/button interactions inside
            the editor body while keeping tab navigation usable above. */}
        <div className={isReadOnly ? "[&_input]:pointer-events-none [&_textarea]:pointer-events-none [&_button]:pointer-events-none [&_[role=combobox]]:pointer-events-none [&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed select-text" : ""} aria-disabled={isReadOnly}>

        {/* OVERVIEW */}
        <TabsContent value="overview">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader icon={Info} title="Trip Overview" subtitle="Essential details your client sees first" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Trip title" className="md:col-span-2">
                <Input value={itin.title} onChange={(e) => update({ title: e.target.value })} className={inputCls} data-testid="overview-title" />
              </Field>
              <Field label="Client name *">
                <Input required value={itin.client_name} onChange={(e) => update({ client_name: e.target.value })} className={inputCls} data-testid="overview-client-name" />
              </Field>
              <Field label="Client email *">
                <Input required type="email" value={itin.client_email} onChange={(e) => update({ client_email: e.target.value })} className={inputCls} data-testid="overview-client-email" />
              </Field>
              <Field label="Client phone">
                <Input type="tel" value={itin.client_phone || ""} onChange={(e) => update({ client_phone: e.target.value })} className={inputCls} placeholder="+1 555 555 0100" data-testid="overview-client-phone" />
              </Field>
              <Field label="Destination *">
                <Input required value={itin.destination} onChange={(e) => update({ destination: e.target.value })} className={inputCls} placeholder="e.g. Positano, Italy" data-testid="overview-destination" />
              </Field>
              <Field label="Cover image URL">
                <Input value={itin.cover_image_url} onChange={(e) => update({ cover_image_url: e.target.value })} className={inputCls} data-testid="overview-cover" />
              </Field>
              <Field label="Start date *">
                <Input required type="date" value={itin.start_date} onChange={(e) => update({ start_date: e.target.value })} className={inputCls} data-testid="overview-start" />
              </Field>
              <Field label="End date *">
                <Input required type="date" value={itin.end_date} onChange={(e) => update({ end_date: e.target.value })} className={inputCls} data-testid="overview-end" />
              </Field>
              <Field label="Travelers">
                <Input
                  type="number"
                  min={0}
                  value={itin.traveler_count ?? ""}
                  onChange={(e) => update({ traveler_count: parseInt(e.target.value, 10) || 0 })}
                  className={inputCls}
                  placeholder="2"
                  data-testid="overview-travelers"
                />
              </Field>
              <Field label="Description / welcome note" className="md:col-span-2">
                <Textarea rows={4} value={itin.description} onChange={(e) => update({ description: e.target.value })} className={inputCls} placeholder="Dear traveler, welcome to your curated journey…" data-testid="overview-description" />
              </Field>

              <Field label="What's included (one per line)" className="md:col-span-1">
                <Textarea
                  rows={6}
                  value={(itin.inclusions || []).join("\n")}
                  onChange={(e) => update({ inclusions: e.target.value.split("\n") })}
                  className={inputCls}
                  placeholder={"Round-trip business class flights\nDaily breakfast\nAll private transfers\nVIP airport meet & greet"}
                  data-testid="overview-inclusions"
                />
              </Field>
              <Field label="Not included (one per line)" className="md:col-span-1">
                <Textarea
                  rows={6}
                  value={(itin.exclusions || []).join("\n")}
                  onChange={(e) => update({ exclusions: e.target.value.split("\n") })}
                  className={inputCls}
                  placeholder={"Travel insurance\nGratuities\nMeals not specified\nPersonal expenses"}
                  data-testid="overview-exclusions"
                />
              </Field>
              <Field label="Terms & notes" className="md:col-span-2">
                <Textarea
                  rows={3}
                  value={itin.terms || ""}
                  onChange={(e) => update({ terms: e.target.value })}
                  className={inputCls}
                  placeholder="Cancellation policy, deposit requirements, force-majeure clauses, etc."
                  data-testid="overview-terms"
                />
              </Field>

              <Field
                label="Internal Research / Notes — NOT included in client PDF"
                className="md:col-span-2"
                hint="Use this scratchpad for Google research, supplier contacts, pasted reference docs, and anything else only you should see. It stays out of the PDF and the public share view."
              >
                <Textarea
                  rows={5}
                  value={itin.internal_notes || ""}
                  onChange={(e) => update({ internal_notes: e.target.value })}
                  className={`${inputCls} border-amber-400/20 bg-amber-500/[0.03]`}
                  placeholder={"Paste Google research, supplier links, agent reminders, anything internal.\nThis is for you — clients never see this."}
                  data-testid="overview-internal-notes"
                />
              </Field>

              {itin.cover_image_url && (
                <div className="md:col-span-2 rounded-lg overflow-hidden border border-white/10">
                  <img src={itin.cover_image_url} alt="Cover" className="w-full aspect-[21/9] object-cover" />
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* DAY BY DAY */}
        <TabsContent value="days">
          <div className="mb-5">
            <SectionHeader icon={CalendarBlank} title="Day by Day" subtitle="Build the journey one day at a time — flights, hotels, tours, dining, free time" />
            <DayByDayTab
              days={itin.days || []}
              onChange={(days) => update({ days })}
              startDate={itin.start_date}
              destination={itin.destination}
            />
          </div>
        </TabsContent>

        {/* FLIGHTS */}
        <TabsContent value="flights">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={Airplane} title="Flights" subtitle="Add each segment of your client's journey"
              action={
                <Button onClick={() => addTo("flights", { airline: "", flight_number: "", from_airport: "", to_airport: "", from_city: "", to_city: "", departure_date: "", departure_time: "", arrival_date: "", arrival_time: "", cabin_class: "Economy", confirmation_number: "", supplier_contact: "", price: "", currency: "USD", link: "", notes: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-flight-btn">
                  <Plus size={16} weight="bold" /> Add flight
                </Button>
              }
            />
            {(itin.flights || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No flights yet. Add your first segment.</p>}
            {(itin.flights || []).map((f, i) => (
              <ItemCard key={f.id || i} index={i} onRemove={() => removeFromList("flights", i)} testid={`flight-card-${i}`}>
                <div className="mb-4 flex justify-end">
                  <Button
                    type="button"
                    onClick={() => setLookupIdx(i)}
                    variant="outline"
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] bg-transparent h-9 text-xs uppercase tracking-widest"
                    data-testid={`flight-lookup-btn-${i}`}
                  >
                    <MagnifyingGlass size={14} weight="duotone" /> Lookup flight
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Airline"><Input value={f.airline || ""} onChange={(e) => updateList("flights", i, { airline: e.target.value })} className={inputCls} placeholder="Emirates" data-testid={`flight-airline-${i}`} /></Field>
                  <Field label="Flight #"><Input value={f.flight_number || ""} onChange={(e) => updateList("flights", i, { flight_number: e.target.value })} className={inputCls} placeholder="EK 202" data-testid={`flight-number-${i}`} /></Field>
                  <Field label="Cabin / Class">
                    <Select value={f.cabin_class || "Economy"} onValueChange={(v) => updateList("flights", i, { cabin_class: v })}>
                      <SelectTrigger className={inputCls} data-testid={`flight-cabin-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white">
                        {["Economy", "Premium Economy", "Business", "First"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="From city"><Input value={f.from_city || ""} onChange={(e) => updateList("flights", i, { from_city: e.target.value })} className={inputCls} placeholder="New York" /></Field>
                  <Field label="From airport"><Input value={f.from_airport || ""} onChange={(e) => updateList("flights", i, { from_airport: e.target.value })} className={inputCls} placeholder="JFK" data-testid={`flight-from-airport-${i}`} /></Field>
                  <Field label=""><span className="text-[10px] text-white/30 uppercase tracking-widest">&nbsp;</span></Field>

                  <Field label="Departure date"><Input type="date" value={f.departure_date || ""} onChange={(e) => updateList("flights", i, { departure_date: e.target.value })} className={inputCls} data-testid={`flight-dep-date-${i}`} /></Field>
                  <Field label="Departure time"><Input type="time" value={f.departure_time || ""} onChange={(e) => updateList("flights", i, { departure_time: e.target.value })} className={inputCls} data-testid={`flight-dep-time-${i}`} /></Field>
                  {(!f.departure_date && f.departure) ? (
                    <Field label="Departure (legacy)"><Input type="datetime-local" value={f.departure} onChange={(e) => updateList("flights", i, { departure: e.target.value })} className={inputCls} /></Field>
                  ) : <Field label=""><span className="text-[10px] text-white/30 uppercase tracking-widest">&nbsp;</span></Field>}

                  <Field label="To city"><Input value={f.to_city || ""} onChange={(e) => updateList("flights", i, { to_city: e.target.value })} className={inputCls} placeholder="Rome" /></Field>
                  <Field label="To airport"><Input value={f.to_airport || ""} onChange={(e) => updateList("flights", i, { to_airport: e.target.value })} className={inputCls} placeholder="FCO" data-testid={`flight-to-airport-${i}`} /></Field>
                  <Field label=""><span className="text-[10px] text-white/30 uppercase tracking-widest">&nbsp;</span></Field>

                  <Field label="Arrival date"><Input type="date" value={f.arrival_date || ""} onChange={(e) => updateList("flights", i, { arrival_date: e.target.value })} className={inputCls} data-testid={`flight-arr-date-${i}`} /></Field>
                  <Field label="Arrival time"><Input type="time" value={f.arrival_time || ""} onChange={(e) => updateList("flights", i, { arrival_time: e.target.value })} className={inputCls} data-testid={`flight-arr-time-${i}`} /></Field>
                  {(!f.arrival_date && f.arrival) ? (
                    <Field label="Arrival (legacy)"><Input type="datetime-local" value={f.arrival} onChange={(e) => updateList("flights", i, { arrival: e.target.value })} className={inputCls} /></Field>
                  ) : <Field label=""><span className="text-[10px] text-white/30 uppercase tracking-widest">&nbsp;</span></Field>}

                  <Field label="Confirmation #"><Input value={f.confirmation_number || ""} onChange={(e) => updateList("flights", i, { confirmation_number: e.target.value })} className={inputCls} data-testid={`flight-confirmation-${i}`} /></Field>
                  <Field label="Supplier / contact"><Input value={f.supplier_contact || ""} onChange={(e) => updateList("flights", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Sabre desk · agent@email" data-testid={`flight-supplier-${i}`} /></Field>
                  <Field label="Document / Attachment"><Input value={f.link || ""} onChange={(e) => updateList("flights", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or booking link" data-testid={`flight-link-${i}`} /></Field>

                  <Field label="Price"><Input value={f.price || ""} onChange={(e) => updateList("flights", i, { price: e.target.value })} className={inputCls} placeholder="1250.00" data-testid={`flight-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={f.currency || "USD"} onValueChange={(v) => updateList("flights", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`flight-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {["USD","EUR","GBP","AUD","CAD","JPY","SGD","HKD","CHF","NZD","AED","INR","CNY","MXN","PHP","BRL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label=""><span className="text-[10px] text-white/30 uppercase tracking-widest">&nbsp;</span></Field>

                  <Field label="Notes" className="md:col-span-3"><Input value={f.notes || ""} onChange={(e) => updateList("flights", i, { notes: e.target.value })} className={inputCls} placeholder="Seat preference, meal, etc." /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* HOTELS */}
        <TabsContent value="hotels">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={BuildingApartment} title="Hotels" subtitle="Accommodations for the journey"
              action={
                <Button onClick={() => addTo("hotels", { name: "", location: "", address: "", check_in: "", check_out: "", check_in_time: "", check_out_time: "", room_type: "", confirmation_number: "", supplier_contact: "", price: "", currency: "USD", link: "", image_url: "", notes: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-hotel-btn">
                  <Plus size={16} weight="bold" /> Add hotel
                </Button>
              }
            />
            {(itin.hotels || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No hotels yet.</p>}
            {(itin.hotels || []).map((h, i) => (
              <ItemCard key={h.id || i} index={i} onRemove={() => removeFromList("hotels", i)} testid={`hotel-card-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Hotel name"><Input value={h.name || ""} onChange={(e) => updateList("hotels", i, { name: e.target.value })} className={inputCls} placeholder="Le Sirenuse" data-testid={`hotel-name-${i}`} /></Field>
                  <Field label="Room type"><Input value={h.room_type || ""} onChange={(e) => updateList("hotels", i, { room_type: e.target.value })} className={inputCls} placeholder="Sea View Suite" data-testid={`hotel-room-${i}`} /></Field>

                  <Field label="Check-in date"><Input type="date" value={h.check_in || ""} onChange={(e) => updateList("hotels", i, { check_in: e.target.value })} className={inputCls} data-testid={`hotel-checkin-date-${i}`} /></Field>
                  <Field label="Check-in time"><Input type="time" value={h.check_in_time || ""} onChange={(e) => updateList("hotels", i, { check_in_time: e.target.value })} className={inputCls} data-testid={`hotel-checkin-time-${i}`} /></Field>
                  <Field label="Check-out date"><Input type="date" value={h.check_out || ""} onChange={(e) => updateList("hotels", i, { check_out: e.target.value })} className={inputCls} data-testid={`hotel-checkout-date-${i}`} /></Field>
                  <Field label="Check-out time"><Input type="time" value={h.check_out_time || ""} onChange={(e) => updateList("hotels", i, { check_out_time: e.target.value })} className={inputCls} data-testid={`hotel-checkout-time-${i}`} /></Field>

                  <Field label="Address / location" className="md:col-span-2"><Input value={h.address || h.location || ""} onChange={(e) => updateList("hotels", i, { address: e.target.value })} className={inputCls} placeholder="Via C. Colombo, 30, 84017 Positano SA, Italy" data-testid={`hotel-address-${i}`} /></Field>

                  <Field label="Confirmation #"><Input value={h.confirmation_number || ""} onChange={(e) => updateList("hotels", i, { confirmation_number: e.target.value })} className={inputCls} data-testid={`hotel-confirmation-${i}`} /></Field>
                  <Field label="Supplier / contact"><Input value={h.supplier_contact || ""} onChange={(e) => updateList("hotels", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Booking.com · agent@email" data-testid={`hotel-supplier-${i}`} /></Field>

                  <Field label="Price"><Input value={h.price || ""} onChange={(e) => updateList("hotels", i, { price: e.target.value })} className={inputCls} placeholder="2500.00" data-testid={`hotel-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={h.currency || "USD"} onValueChange={(v) => updateList("hotels", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`hotel-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {["USD","EUR","GBP","AUD","CAD","JPY","SGD","HKD","CHF","NZD","AED","INR","CNY","MXN","PHP","BRL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Document / Attachment" className="md:col-span-2"><Input value={h.link || ""} onChange={(e) => updateList("hotels", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or booking link" data-testid={`hotel-link-${i}`} /></Field>
                  <Field label="Image URL" className="md:col-span-2"><Input value={h.image_url || ""} onChange={(e) => updateList("hotels", i, { image_url: e.target.value })} className={inputCls} /></Field>
                  <Field label="Notes" className="md:col-span-2"><Input value={h.notes || ""} onChange={(e) => updateList("hotels", i, { notes: e.target.value })} className={inputCls} placeholder="Breakfast included, etc." /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* TOURS */}
        <TabsContent value="tours">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={MapTrifold} title="Tours & Experiences" subtitle="Curated activities"
              action={
                <Button onClick={() => addTo("tours", { title: "", location: "", date: "", duration: "", provider: "", description: "", image_url: "", confirmation_number: "", supplier_contact: "", price: "", currency: "USD", link: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-tour-btn">
                  <Plus size={16} weight="bold" /> Add tour
                </Button>
              }
            />
            {(itin.tours || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No tours yet.</p>}
            {(itin.tours || []).map((t, i) => (
              <ItemCard key={t.id || i} index={i} onRemove={() => removeFromList("tours", i)} testid={`tour-card-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Title"><Input value={t.title} onChange={(e) => updateList("tours", i, { title: e.target.value })} className={inputCls} placeholder="Private Amalfi Boat Tour" /></Field>
                  <Field label="Location"><Input value={t.location} onChange={(e) => updateList("tours", i, { location: e.target.value })} className={inputCls} /></Field>
                  <Field label="Date"><Input type="date" value={t.date} onChange={(e) => updateList("tours", i, { date: e.target.value })} className={inputCls} /></Field>
                  <Field label="Duration"><Input value={t.duration} onChange={(e) => updateList("tours", i, { duration: e.target.value })} className={inputCls} placeholder="4 hours" /></Field>
                  <Field label="Provider" className="md:col-span-2"><Input value={t.provider} onChange={(e) => updateList("tours", i, { provider: e.target.value })} className={inputCls} /></Field>
                  <Field label="Confirmation #"><Input value={t.confirmation_number || ""} onChange={(e) => updateList("tours", i, { confirmation_number: e.target.value })} className={inputCls} data-testid={`tour-confirmation-${i}`} /></Field>
                  <Field label="Supplier / contact"><Input value={t.supplier_contact || ""} onChange={(e) => updateList("tours", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Viator · agent@email" data-testid={`tour-supplier-${i}`} /></Field>
                  <Field label="Price"><Input value={t.price || ""} onChange={(e) => updateList("tours", i, { price: e.target.value })} className={inputCls} placeholder="350.00" data-testid={`tour-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={t.currency || "USD"} onValueChange={(v) => updateList("tours", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`tour-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Document / Attachment" className="md:col-span-2"><Input value={t.link || ""} onChange={(e) => updateList("tours", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or booking link" data-testid={`tour-link-${i}`} /></Field>
                  <Field label="Image URL" className="md:col-span-2"><Input value={t.image_url} onChange={(e) => updateList("tours", i, { image_url: e.target.value })} className={inputCls} /></Field>
                  <Field label="Description" className="md:col-span-2"><Textarea rows={3} value={t.description} onChange={(e) => updateList("tours", i, { description: e.target.value })} className={inputCls} /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* CRUISE */}
        <TabsContent value="cruise">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={Boat} title="Cruises" subtitle="Sea voyages for your client"
              action={
                <Button onClick={() => addTo("cruises", { line_name: "", ship_name: "", from_port: "", to_port: "", departure: "", arrival: "", cabin_type: "", image_url: "", notes: "", confirmation_number: "", supplier_contact: "", price: "", currency: "USD", link: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-cruise-btn">
                  <Plus size={16} weight="bold" /> Add cruise
                </Button>
              }
            />
            {(itin.cruises || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No cruises yet.</p>}
            {(itin.cruises || []).map((c, i) => (
              <ItemCard key={c.id || i} index={i} onRemove={() => removeFromList("cruises", i)} testid={`cruise-card-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Cruise line"><Input value={c.line_name} onChange={(e) => updateList("cruises", i, { line_name: e.target.value })} className={inputCls} placeholder="Silversea" /></Field>
                  <Field label="Ship name"><Input value={c.ship_name} onChange={(e) => updateList("cruises", i, { ship_name: e.target.value })} className={inputCls} placeholder="Silver Muse" /></Field>
                  <Field label="From port"><Input value={c.from_port} onChange={(e) => updateList("cruises", i, { from_port: e.target.value })} className={inputCls} /></Field>
                  <Field label="To port"><Input value={c.to_port} onChange={(e) => updateList("cruises", i, { to_port: e.target.value })} className={inputCls} /></Field>
                  <Field label="Departure"><Input type="datetime-local" value={c.departure} onChange={(e) => updateList("cruises", i, { departure: e.target.value })} className={inputCls} /></Field>
                  <Field label="Arrival"><Input type="datetime-local" value={c.arrival} onChange={(e) => updateList("cruises", i, { arrival: e.target.value })} className={inputCls} /></Field>
                  <Field label="Cabin type"><Input value={c.cabin_type} onChange={(e) => updateList("cruises", i, { cabin_type: e.target.value })} className={inputCls} placeholder="Vista Suite" /></Field>
                  <Field label="Image URL"><Input value={c.image_url} onChange={(e) => updateList("cruises", i, { image_url: e.target.value })} className={inputCls} /></Field>
                  <Field label="Confirmation #"><Input value={c.confirmation_number || ""} onChange={(e) => updateList("cruises", i, { confirmation_number: e.target.value })} className={inputCls} data-testid={`cruise-confirmation-${i}`} /></Field>
                  <Field label="Supplier / contact"><Input value={c.supplier_contact || ""} onChange={(e) => updateList("cruises", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Royal Caribbean · agent@email" data-testid={`cruise-supplier-${i}`} /></Field>
                  <Field label="Price"><Input value={c.price || ""} onChange={(e) => updateList("cruises", i, { price: e.target.value })} className={inputCls} placeholder="4500.00" data-testid={`cruise-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={c.currency || "USD"} onValueChange={(v) => updateList("cruises", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`cruise-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {CURRENCIES.map((cc) => <SelectItem key={cc} value={cc}>{cc}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Document / Attachment" className="md:col-span-2"><Input value={c.link || ""} onChange={(e) => updateList("cruises", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or booking link" data-testid={`cruise-link-${i}`} /></Field>
                  <Field label="Notes" className="md:col-span-2"><Input value={c.notes} onChange={(e) => updateList("cruises", i, { notes: e.target.value })} className={inputCls} /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* TRANSPORTATION */}
        <TabsContent value="transportation">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={Car} title="Transportation" subtitle="Transfers, car rentals, trains & more"
              action={
                <Button onClick={() => addTo("transportation", { type: "Private Transfer", provider: "", pickup_location: "", dropoff_location: "", pickup_datetime: "", dropoff_datetime: "", vehicle: "", confirmation_number: "", notes: "", image_url: "", supplier_contact: "", price: "", currency: "USD", link: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-transportation-btn">
                  <Plus size={16} weight="bold" /> Add transport
                </Button>
              }
            />
            {(itin.transportation || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No ground transportation yet.</p>}
            {(itin.transportation || []).map((t, i) => (
              <ItemCard key={t.id || i} index={i} onRemove={() => removeFromList("transportation", i)} testid={`transportation-card-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Type">
                    <Select value={t.type || "Private Transfer"} onValueChange={(v) => updateList("transportation", i, { type: v })}>
                      <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white">
                        {["Private Transfer", "Car Rental", "Chauffeur", "Train", "Bus", "Ferry"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Provider"><Input value={t.provider} onChange={(e) => updateList("transportation", i, { provider: e.target.value })} className={inputCls} placeholder="Blacklane, Hertz, etc." /></Field>
                  <Field label="Pickup location"><Input value={t.pickup_location} onChange={(e) => updateList("transportation", i, { pickup_location: e.target.value })} className={inputCls} placeholder="Rome FCO Airport" /></Field>
                  <Field label="Drop-off location"><Input value={t.dropoff_location} onChange={(e) => updateList("transportation", i, { dropoff_location: e.target.value })} className={inputCls} placeholder="Hotel Le Sirenuse" /></Field>
                  <Field label="Pickup date/time"><Input type="datetime-local" value={t.pickup_datetime} onChange={(e) => updateList("transportation", i, { pickup_datetime: e.target.value })} className={inputCls} /></Field>
                  <Field label="Return date/time"><Input type="datetime-local" value={t.dropoff_datetime} onChange={(e) => updateList("transportation", i, { dropoff_datetime: e.target.value })} className={inputCls} /></Field>
                  <Field label="Vehicle"><Input value={t.vehicle} onChange={(e) => updateList("transportation", i, { vehicle: e.target.value })} className={inputCls} placeholder="Mercedes S-Class" /></Field>
                  <Field label="Confirmation #"><Input value={t.confirmation_number} onChange={(e) => updateList("transportation", i, { confirmation_number: e.target.value })} className={inputCls} /></Field>
                  <Field label="Supplier / contact"><Input value={t.supplier_contact || ""} onChange={(e) => updateList("transportation", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Blacklane · ops@email" data-testid={`transport-supplier-${i}`} /></Field>
                  <Field label="Price"><Input value={t.price || ""} onChange={(e) => updateList("transportation", i, { price: e.target.value })} className={inputCls} placeholder="180.00" data-testid={`transport-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={t.currency || "USD"} onValueChange={(v) => updateList("transportation", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`transport-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Document / Attachment"><Input value={t.link || ""} onChange={(e) => updateList("transportation", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or booking link" data-testid={`transport-link-${i}`} /></Field>
                  <Field label="Image URL" className="md:col-span-2"><Input value={t.image_url} onChange={(e) => updateList("transportation", i, { image_url: e.target.value })} className={inputCls} /></Field>
                  <Field label="Notes" className="md:col-span-2"><Input value={t.notes} onChange={(e) => updateList("transportation", i, { notes: e.target.value })} className={inputCls} placeholder="Driver will meet at arrivals hall holding name sign" /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* TRAVEL PROTECTION */}
        <TabsContent value="protection">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={ShieldCheck} title="Travel Protection" subtitle="Insurance & peace of mind for your client"
              action={
                <Button onClick={() => addTo("protection", { provider: "", plan_name: "", policy_number: "", coverage_amount: "", start_date: "", end_date: "", coverage_details: "", emergency_contact: "", notes: "", supplier_contact: "", price: "", currency: "USD", link: "" })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-protection-btn">
                  <Plus size={16} weight="bold" /> Add plan
                </Button>
              }
            />
            {(itin.protection || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No protection plans yet.</p>}
            {(itin.protection || []).map((p, i) => (
              <ItemCard key={p.id || i} index={i} onRemove={() => removeFromList("protection", i)} testid={`protection-card-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Insurance provider"><Input value={p.provider} onChange={(e) => updateList("protection", i, { provider: e.target.value })} className={inputCls} placeholder="Allianz, Travel Guard" /></Field>
                  <Field label="Plan name"><Input value={p.plan_name} onChange={(e) => updateList("protection", i, { plan_name: e.target.value })} className={inputCls} placeholder="Platinum Plan" /></Field>
                  <Field label="Policy number"><Input value={p.policy_number} onChange={(e) => updateList("protection", i, { policy_number: e.target.value })} className={inputCls} /></Field>
                  <Field label="Coverage amount"><Input value={p.coverage_amount} onChange={(e) => updateList("protection", i, { coverage_amount: e.target.value })} className={inputCls} placeholder="$100,000" /></Field>
                  <Field label="Start date"><Input type="date" value={p.start_date} onChange={(e) => updateList("protection", i, { start_date: e.target.value })} className={inputCls} /></Field>
                  <Field label="End date"><Input type="date" value={p.end_date} onChange={(e) => updateList("protection", i, { end_date: e.target.value })} className={inputCls} /></Field>
                  <Field label="Emergency contact" className="md:col-span-2"><Input value={p.emergency_contact} onChange={(e) => updateList("protection", i, { emergency_contact: e.target.value })} className={inputCls} placeholder="+1 800 555 0199 (24/7)" /></Field>
                  <Field label="Supplier / contact"><Input value={p.supplier_contact || ""} onChange={(e) => updateList("protection", i, { supplier_contact: e.target.value })} className={inputCls} placeholder="Allianz · agent@email" data-testid={`protection-supplier-${i}`} /></Field>
                  <Field label="Price"><Input value={p.price || ""} onChange={(e) => updateList("protection", i, { price: e.target.value })} className={inputCls} placeholder="120.00" data-testid={`protection-price-${i}`} /></Field>
                  <Field label="Currency">
                    <Select value={p.currency || "USD"} onValueChange={(v) => updateList("protection", i, { currency: v })}>
                      <SelectTrigger className={inputCls} data-testid={`protection-currency-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                        {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Document / Attachment"><Input value={p.link || ""} onChange={(e) => updateList("protection", i, { link: e.target.value })} className={inputCls} placeholder="Google Drive, PDF, or policy document" data-testid={`protection-link-${i}`} /></Field>
                  <Field label="Coverage details" className="md:col-span-2"><Textarea rows={3} value={p.coverage_details} onChange={(e) => updateList("protection", i, { coverage_details: e.target.value })} className={inputCls} placeholder="Trip cancellation, medical, baggage loss…" /></Field>
                  <Field label="Notes" className="md:col-span-2"><Input value={p.notes} onChange={(e) => updateList("protection", i, { notes: e.target.value })} className={inputCls} /></Field>
                </div>
              </ItemCard>
            ))}
          </div>
        </TabsContent>

        {/* PRICING */}
        <TabsContent value="pricing">
          <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
            <SectionHeader
              icon={CurrencyDollar} title="Pricing" subtitle="Transparent breakdown for your client"
              action={
                <Button onClick={() => updatePricing({ items: [...(itin.pricing.items || []), { id: newId(), label: "", amount: 0 }] })}
                  className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9" data-testid="add-pricing-item-btn">
                  <Plus size={16} weight="bold" /> Line item
                </Button>
              }
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Field label="Currency">
                <Select value={itin.pricing.currency || "USD"} onValueChange={(v) => updatePricing({ currency: v })}>
                  <SelectTrigger className={inputCls} data-testid="pricing-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white">
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Deposit required">
                <Input type="number" step="0.01" value={itin.pricing.deposit || 0} onChange={(e) => updatePricing({ deposit: parseFloat(e.target.value) || 0 })} className={inputCls} data-testid="pricing-deposit" />
              </Field>
              <div className="flex items-end">
                <div className="flex-1 border border-[#D4AF37]/40 bg-[#D4AF37]/5 rounded-md px-4 py-2">
                  <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-sans">Total</p>
                  <p className="font-serif text-2xl text-white" data-testid="pricing-total">
                    {itin.pricing.currency} {pricingTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6 space-y-4">
              <div className="rounded-md border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-5" data-testid="pricing-suppliers-pointer">
                <div className="flex items-start gap-3">
                  <Storefront size={22} weight="duotone" className="text-[#D4AF37] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-sans mb-1">Supplier payment links</p>
                    <p className="text-sm text-white/80 leading-relaxed mb-3">
                      Manage <strong className="text-white">up to 5 supplier payment links</strong> (Hotel, Flight, Cruise, Tour, Transport, Insurance, Other) in the dedicated <strong className="text-[#D4AF37]">Suppliers</strong> tab. Each row gets its own Pay Now or Submit Payment Details flow — clients see a guided "Complete Your Booking" checklist after approval.
                    </p>
                    <Button
                      type="button"
                      onClick={() => setActiveTab("suppliers")}
                      className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-9 text-xs uppercase tracking-widest"
                      data-testid="goto-suppliers-tab-btn"
                    >
                      <Storefront size={14} weight="duotone" /> Open Suppliers tab
                    </Button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Balance payment link (for remaining balance)">
                  <Input
                    value={itin.pricing.balance_payment_link || ""}
                    onChange={(e) => updatePricing({ balance_payment_link: e.target.value })}
                    className={inputCls}
                    placeholder="Optional — used after deposit if you collect a balance separately"
                    data-testid="pricing-balance-link"
                  />
                </Field>
                <Field label="Balance due date">
                  <Input
                    type="date"
                    value={itin.pricing.balance_due_date || ""}
                    onChange={(e) => updatePricing({ balance_due_date: e.target.value })}
                    className={inputCls}
                    data-testid="pricing-balance-due-date"
                  />
                </Field>
              </div>
              <p className="text-[11px] text-white/40 -mt-3 font-sans">
                If empty, balance is due 30 days after deposit is confirmed.
              </p>
            </div>

            {(itin.pricing.items || []).length === 0 && <p className="text-white/40 text-sm italic font-sans">No line items yet. Add the first one.</p>}

            <div className="space-y-3">
              {(itin.pricing.items || []).map((p, i) => (
                <div key={p.id || i} className="grid grid-cols-12 gap-3 items-center" data-testid={`pricing-item-${i}`}>
                  <div className="col-span-7">
                    <Input value={p.label} onChange={(e) => updatePricingItem(i, { label: e.target.value })} className={inputCls} placeholder="e.g. Hotel Le Sirenuse · 5 nights" />
                  </div>
                  <div className="col-span-4">
                    <Input type="number" step="0.01" value={p.amount} onChange={(e) => updatePricingItem(i, { amount: parseFloat(e.target.value) || 0 })} className={inputCls} placeholder="0.00" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <Button size="sm" variant="ghost" onClick={() => removePricingItem(i)} className="text-white/50 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0">
                      <Trash size={15} weight="duotone" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Field label="Pricing notes">
                <Textarea rows={3} value={itin.pricing.notes} onChange={(e) => updatePricing({ notes: e.target.value })} className={inputCls} placeholder="Terms, inclusions, cancellation policy…" data-testid="pricing-notes" />
              </Field>
            </div>
          </div>
        </TabsContent>

        {/* SUPPLIERS */}
        <TabsContent value="suppliers">
          <SuppliersTab itin={itin} update={update} />
        </TabsContent>

        </div>{/* /read-only veil */}
      </Tabs>

      {/* Share panel */}
      <div className="mt-10 border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Share size={22} weight="duotone" className="text-[#D4AF37]" />
          <div>
            <h3 className="font-serif text-xl text-white">Share with your client</h3>
            <p className="text-xs text-white/50 font-sans">Send this private link to view the itinerary on any device.</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input readOnly value={shareUrl} className="bg-black/40 border-white/10 text-white/80 font-mono text-sm" data-testid="share-url-input" />
          <Button onClick={copyShareLink} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-11 px-5" data-testid="copy-link-btn">
            {copied ? <><Check size={16} weight="bold" /> Copied</> : <><ClipboardText size={16} weight="duotone" /> Copy link</>}
          </Button>
        </div>
      </div>

      {/* Payment status panel */}
      <PaymentStatusPanel itin={itin} onChanged={(updated) => setItin(updated)} />

      {/* Flight auto-fill dialog (AeroDataBox) */}
      <FlightLookupDialog
        open={lookupIdx !== null}
        onClose={() => setLookupIdx(null)}
        onPick={(seg) => {
          if (lookupIdx === null) return;
          updateList("flights", lookupIdx, {
            airline: seg.airline || "",
            flight_number: seg.flight_number || "",
            from_airport: seg.from_airport || "",
            from_city: seg.from_city || "",
            to_airport: seg.to_airport || "",
            to_city: seg.to_city || "",
            departure_date: seg.departure_date || "",
            departure_time: seg.departure_time || "",
            arrival_date: seg.arrival_date || "",
            arrival_time: seg.arrival_time || "",
          });
        }}
        defaultFlightNumber={lookupIdx !== null ? (itin.flights?.[lookupIdx]?.flight_number || "") : ""}
        defaultDate={lookupIdx !== null ? (itin.flights?.[lookupIdx]?.departure_date || itin.start_date || "") : ""}
      />
    </AgentLayout>
  );
}

function PaymentStatusPanel({ itin, onChanged }) {
  const [busy, setBusy] = useState(null);
  const status = itin.status || "Draft";
  const supplierLink = (itin.pricing?.supplier_payment_link || itin.pricing?.external_payment_link || "").trim();

  const apply = async (next) => {
    setBusy(next);
    try {
      const { data } = await api.post(`/itineraries/${itin.id}/payment-status`, { status: next });
      onChanged(data);
      toast.success(`Status updated to "${next}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update status");
    } finally {
      setBusy(null);
    }
  };

  const ACTIONS = [
    { id: "Payment Link Sent", label: "Mark Link Sent", icon: Share, hint: "Sends supplier link to client by email", disabled: !supplierLink },
    { id: "Deposit Confirmed", label: "Confirm Deposit", icon: Check, hint: "Mark deposit as received via supplier or terminal" },
    { id: "Paid in Full", label: "Mark Paid in Full", icon: CurrencyDollar, hint: "Mark full payment as received" },
    { id: "Booked", label: "Mark Booked", icon: ShieldCheck, hint: "Final — sends booking confirmation to client" },
  ];

  return (
    <div className="mt-6 border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6" data-testid="agent-payment-panel">
      <div className="flex items-center gap-3 mb-1">
        <CurrencyDollar size={22} weight="duotone" className="text-[#D4AF37]" />
        <h3 className="font-serif text-xl text-white">Payment lifecycle</h3>
      </div>
      <p className="text-xs text-white/50 font-sans mb-4">
        Current status: <span className="text-[#D4AF37] uppercase tracking-widest">{status}</span>
        {!supplierLink && <span className="ml-3 text-amber-300/80">Tip: add a supplier payment link in the Pricing tab.</span>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ACTIONS.map((a) => {
          const Icon = a.icon;
          const isCurrent = status === a.id;
          return (
            <button
              key={a.id}
              type="button"
              disabled={busy !== null || a.disabled || isCurrent}
              onClick={() => apply(a.id)}
              className={`text-left rounded-lg border p-4 transition-all ${
                isCurrent
                  ? "border-[#D4AF37] bg-[#D4AF37]/10"
                  : a.disabled
                  ? "border-white/5 bg-black/20 opacity-40 cursor-not-allowed"
                  : "border-white/10 bg-black/30 hover:border-[#D4AF37]/40 hover:bg-[#D4AF37]/5"
              }`}
              data-testid={`mark-${a.id.replace(/\s+/g, "-").toLowerCase()}-btn`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} weight="duotone" className="text-[#D4AF37]" />
                <p className="font-serif text-sm text-white">{a.label}</p>
              </div>
              <p className="text-[11px] text-white/50 leading-snug">
                {busy === a.id ? "Updating…" : isCurrent ? "Current status" : a.hint}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ===== Suppliers tab =====
const SUPPLIER_CATEGORIES = ["Hotel", "Flight", "Transportation", "Tour", "Cruise", "Insurance", "Other"];
const SUPPLIER_STATUSES = ["Link Added", "Sent to Client", "Paid by Client", "Commission Expected"];
const SUPPLIER_PAYMENT_LABELS = ["", "Deposit Required", "Full Payment", "Optional", "Pay Later"];
const MAX_SUPPLIER_LINKS = 5;

const STATUS_PILL = {
  "Link Added": "bg-white/5 text-white/60 border-white/10",
  "Sent to Client": "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Paid by Client": "bg-emerald-500/10 text-emerald-300 border-emerald-400/40",
  "Commission Expected": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
};

function SuppliersTab({ itin, update }) {
  const links = itin.supplier_links || [];
  const setLinks = (next) => update({ supplier_links: next });

  const addLink = () => {
    if (links.length >= MAX_SUPPLIER_LINKS) {
      toast.error(`Maximum ${MAX_SUPPLIER_LINKS} supplier links per itinerary`);
      return;
    }
    setLinks([
      ...links,
      {
        id: newId(),
        category: "Hotel",
        supplier_name: "",
        payment_link: "",
        notes: "",
        client_note: "",
        payment_label: "",
        payment_mode: "",
        visible_to_client: true,  // default to visible — clients pay through these
        expected_commission: 0,
        commission_currency: itin.pricing?.currency || "USD",
        status: "Link Added",
      },
    ]);
  };

  const moveLink = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const arr = [...links];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setLinks(arr);
  };

  const updateLink = (i, patch) => {
    const arr = [...links];
    arr[i] = { ...arr[i], ...patch };
    setLinks(arr);
  };
  const removeLink = (i) => {
    const arr = [...links];
    arr.splice(i, 1);
    setLinks(arr);
  };

  const totalCommission = links.reduce((s, sl) => s + (parseFloat(sl.expected_commission) || 0), 0);

  return (
    <div className="border border-[#D4AF37]/20 bg-ink-surface rounded-lg p-6 sm:p-8">
      <SectionHeader
        icon={Storefront}
        title="Supplier links & commission"
        subtitle="Track up to 5 supplier payment links and expected commission per booking"
        action={
          <Button
            onClick={addLink}
            disabled={links.length >= MAX_SUPPLIER_LINKS}
            className="bg-[#D4AF37] hover:bg-[#F3C853] text-black text-xs uppercase tracking-widest h-9 disabled:opacity-40"
            data-testid="add-supplier-link-btn"
          >
            <Plus size={16} weight="bold" /> Supplier ({links.length}/{MAX_SUPPLIER_LINKS})
          </Button>
        }
      />

      <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 mb-6 flex items-start gap-2">
        <Info size={16} weight="duotone" className="text-amber-300 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          <strong>Internal data.</strong> Expected commission, status, and notes are visible to you and admins only.
          Clients only see supplier name and payment link — and only when you toggle <em>"Visible to client"</em> on a row.
        </p>
      </div>

      {links.length === 0 && (
        <div className="text-center py-12 border border-dashed border-white/10 rounded-lg">
          <Storefront size={32} weight="duotone" className="text-[#D4AF37]/40 mx-auto mb-2" />
          <p className="text-white/50 text-sm">No supplier links yet. Add your first to start tracking commission.</p>
        </div>
      )}

      <div className="space-y-4">
        {links.map((sl, i) => (
          <div key={sl.id || i} className="border border-[#D4AF37]/20 bg-black/30 rounded-lg p-5" data-testid={`supplier-link-${i}`}>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-sans">#{i + 1}</span>
                <div className="flex items-center gap-1 ml-2">
                  <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => moveLink(i, -1)} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-7 w-7 p-0 disabled:opacity-30" data-testid={`supplier-up-${i}`} aria-label="Move up">
                    <ArrowUp size={13} weight="bold" />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={i === links.length - 1} onClick={() => moveLink(i, 1)} className="text-white/40 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 h-7 w-7 p-0 disabled:opacity-30" data-testid={`supplier-down-${i}`} aria-label="Move down">
                    <ArrowDown size={13} weight="bold" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => updateLink(i, { visible_to_client: !sl.visible_to_client })}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border transition-all ${
                    sl.visible_to_client
                      ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/40"
                      : "bg-white/5 text-white/50 border-white/10 hover:border-white/30"
                  }`}
                  data-testid={`toggle-visible-${i}`}
                  title={sl.visible_to_client ? "Visible to client — click to hide" : "Hidden from client — click to make visible"}
                >
                  {sl.visible_to_client ? <Eye size={12} weight="duotone" /> : <EyeSlash size={12} weight="duotone" />}
                  {sl.visible_to_client ? "Visible to client" : "Internal only"}
                </button>
                <Button size="sm" variant="ghost" onClick={() => removeLink(i)} className="text-white/50 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid={`remove-supplier-${i}`}>
                  <Trash size={14} weight="duotone" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-3">
                <Field label="Category">
                  <Select value={sl.category || "Other"} onValueChange={(v) => updateLink(i, { category: v })}>
                    <SelectTrigger className={inputCls} data-testid={`supplier-category-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      {SUPPLIER_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label="Supplier name">
                  <Input value={sl.supplier_name || ""} onChange={(e) => updateLink(i, { supplier_name: e.target.value })} className={inputCls} placeholder="e.g. Sandals Resorts" data-testid={`supplier-name-${i}`} />
                </Field>
              </div>
              <div className="md:col-span-5">
                <Field label="Payment / booking link">
                  <Input value={sl.payment_link || ""} onChange={(e) => updateLink(i, { payment_link: e.target.value })} className={inputCls} placeholder="https://..." data-testid={`supplier-link-url-${i}`} />
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-3">
                <Field label="Payment label (visible to client)">
                  <Select value={sl.payment_label || "__none__"} onValueChange={(v) => updateLink(i, { payment_label: v === "__none__" ? "" : v })}>
                    <SelectTrigger className={inputCls} data-testid={`supplier-label-${i}`}>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      <SelectItem value="__none__">None</SelectItem>
                      {SUPPLIER_PAYMENT_LABELS.filter(Boolean).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label="Payment mode">
                  <Select value={sl.payment_mode || "__auto__"} onValueChange={(v) => updateLink(i, { payment_mode: v === "__auto__" ? "" : v })}>
                    <SelectTrigger className={inputCls} data-testid={`supplier-mode-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      <SelectItem value="__auto__">Auto (based on link)</SelectItem>
                      <SelectItem value="direct">Client Pays Directly</SelectItem>
                      <SelectItem value="authorization">Requires Authorization</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-6">
                <Field label="Client note (visible to client)">
                  <Input value={sl.client_note || ""} onChange={(e) => updateLink(i, { client_note: e.target.value })} className={inputCls} placeholder='e.g. "Guest must pay deposit at hotel check-in"' data-testid={`supplier-client-note-${i}`} />
                </Field>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-3">
              <div className="md:col-span-3">
                <Field label={<><span className="text-[#D4AF37]">[Internal]</span> Expected commission</>}>
                  <Input type="number" step="0.01" value={sl.expected_commission || 0} onChange={(e) => updateLink(i, { expected_commission: parseFloat(e.target.value) || 0 })} className={inputCls} placeholder="0.00" data-testid={`supplier-commission-${i}`} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Currency">
                  <Select value={sl.commission_currency || "USD"} onValueChange={(v) => updateLink(i, { commission_currency: v })}>
                    <SelectTrigger className={inputCls} data-testid={`supplier-currency-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-3">
                <Field label={<><span className="text-[#D4AF37]">[Internal]</span> Status</>}>
                  <Select value={sl.status || "Link Added"} onValueChange={(v) => updateLink(i, { status: v })}>
                    <SelectTrigger className={inputCls} data-testid={`supplier-status-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-ink-surface border-white/10 text-white">
                      {SUPPLIER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="md:col-span-4">
                <Field label={<><span className="text-[#D4AF37]">[Internal]</span> Notes (private)</>}>
                  <Input value={sl.notes || ""} onChange={(e) => updateLink(i, { notes: e.target.value })} className={inputCls} placeholder="Confirmation #, contact, etc." data-testid={`supplier-notes-${i}`} />
                </Field>
              </div>
            </div>
          </div>
        ))}
      </div>

      {links.length > 0 && (
        <div className="mt-8 border-t border-[#D4AF37]/20 pt-6" data-testid="supplier-summary">
          <h4 className="font-serif text-xl text-white mb-3 flex items-center gap-2">
            <CurrencyDollar size={20} weight="duotone" className="text-[#D4AF37]" />
            Booking summary <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/80 font-sans">[Internal only]</span>
          </h4>
          <div className="overflow-x-auto rounded-md border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-[10px] uppercase tracking-widest text-white/50">
                <tr>
                  <th className="text-left px-4 py-2.5 font-sans">Category</th>
                  <th className="text-left px-4 py-2.5 font-sans">Supplier</th>
                  <th className="text-left px-4 py-2.5 font-sans">Link</th>
                  <th className="text-right px-4 py-2.5 font-sans">Expected commission</th>
                  <th className="text-left px-4 py-2.5 font-sans">Status</th>
                </tr>
              </thead>
              <tbody>
                {links.map((sl, i) => (
                  <tr key={sl.id || i} className="border-t border-white/5">
                    <td className="px-4 py-2.5 text-white/80">{sl.category || "Other"}</td>
                    <td className="px-4 py-2.5 text-white">{sl.supplier_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      {sl.payment_link ? (
                        <a href={sl.payment_link} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#F3C853] text-xs flex items-center gap-1">
                          <ArrowSquareOut size={12} weight="duotone" /> Open
                        </a>
                      ) : <span className="text-white/30 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-white/90">
                      {sl.commission_currency} {(parseFloat(sl.expected_commission) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${STATUS_PILL[sl.status] || STATUS_PILL["Link Added"]}`}>
                        {sl.status || "Link Added"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-[#D4AF37]/30 bg-[#D4AF37]/5">
                  <td colSpan={3} className="px-4 py-3 text-[10px] uppercase tracking-widest text-[#D4AF37] font-sans">
                    Total expected commission
                  </td>
                  <td className="px-4 py-3 text-right font-serif text-lg text-[#D4AF37]" data-testid="supplier-total-commission">
                    {(itin.pricing?.currency || "USD")} {totalCommission.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-[11px] text-white/40 mt-3 italic">
            Clients never see this table. Mixed currencies are summed at face value — convert before reporting if needed.
          </p>
        </div>
      )}
    </div>
  );
}
